import { useState } from 'react';
import { motion } from 'framer-motion';
import { ShoppingBag, ShoppingCart, Star } from 'lucide-react';

const AMAZON_LINK = 'https://www.amazon.com/Divine-Intervention-Journal-Gratitude-Journey/dp/B0GTLJ2LD1';
const SHOPIFY_LINK = 'https://cuavena.myshopify.com';
const JOURNAL_COVER = 'https://customer-assets.emergentagent.com/job_faith-reads-3/artifacts/sfard4sy_886530C1-7F9C-45C7-83B3-BC93BEC6A426.png';
const BOOK_COVER = '/marketing/book-cover-marketing.jpg';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-80px' },
  transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
};

export default function GratitudeJournalPage() {
  return (
    <div data-testid="books-projects-page">

      {/* ===== SECTION 1: HERO ===== */}
      <section className="py-16 md:py-24 bg-[#FDFBF7]">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <motion.div {...fadeUp}>
            <p className="font-sans text-xs tracking-[0.3em] uppercase text-[#A67A5B] mb-4">
              Led By Faith Studios
            </p>
            <h1 className="font-serif text-4xl sm:text-5xl font-semibold text-[#2A2421] mb-6 leading-tight">
              Books &amp; Projects
            </h1>
            <p className="font-sans text-base md:text-lg text-[#7A736E] leading-relaxed max-w-2xl mx-auto">
              A growing collection of faith-rooted books, journals, and purpose-driven projects created to encourage, strengthen, and leave a lasting impact.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ===== SECTION 2: AVAILABLE NOW — DIVINE INTERVENTION ===== */}
      <section className="py-16 md:py-24 bg-white" data-testid="bp-journal">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-center">
            <motion.div {...fadeUp} className="flex justify-center md:justify-end">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-br from-[#C59B76]/20 to-transparent rounded-3xl" />
                <img
                  src={JOURNAL_COVER}
                  alt="Divine Intervention - A 90-Day Faith & Gratitude Journal"
                  data-testid="bp-journal-cover"
                  className="relative rounded-2xl shadow-2xl w-full max-w-[320px]"
                />
              </div>
            </motion.div>
            <motion.div {...fadeUp}>
              <span className="inline-block bg-[#A67A5B] text-white text-xs font-semibold tracking-[0.2em] uppercase px-4 py-1.5 rounded-full mb-5">
                Available Now
              </span>
              <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-[#2A2421] mb-2 leading-tight">
                Divine Intervention
              </h2>
              <p className="font-serif text-lg text-[#A67A5B] italic mb-5">
                A 90-Day Faith &amp; Gratitude Journey
              </p>
              <p className="font-sans text-base text-[#7A736E] leading-relaxed mb-8">
                A guided journal created to draw you closer to God through daily reflection, prayer, and gratitude &mdash; designed to help you build peace, clarity, and spiritual strength one day at a time.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 mb-4">
                <a href={SHOPIFY_LINK} target="_blank" rel="noopener noreferrer" data-testid="bp-buy-shopify" className="inline-flex items-center justify-center gap-2 bg-[#A67A5B] text-white px-7 py-3.5 rounded-full font-medium tracking-wide shadow-md hover:bg-[#8C654A] hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300">
                  <ShoppingBag size={18} />
                  Buy on Shopify
                </a>
                <a href={AMAZON_LINK} target="_blank" rel="noopener noreferrer" data-testid="bp-buy-amazon" className="inline-flex items-center justify-center gap-2 border-2 border-[#A67A5B] text-[#A67A5B] px-7 py-3.5 rounded-full font-medium tracking-wide hover:bg-[#A67A5B] hover:text-white transition-all duration-300">
                  <ShoppingCart size={18} />
                  Buy on Amazon
                </a>
              </div>
              <p className="font-sans text-xs text-[#7A736E]/40 flex items-center gap-1.5">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                Secure checkout on Amazon &amp; Shopify
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ===== SECTION 3: COMING SOON — BOOK ONE ===== */}
      <section className="py-16 md:py-24 bg-[#2A2421] relative overflow-hidden" data-testid="bp-book-one">
        <div className="max-w-6xl mx-auto px-6 md:px-12 relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-center">
            <motion.div {...fadeUp} className="order-2 md:order-1">
              <span className="inline-block border border-[#C59B76]/50 text-[#C59B76] text-xs font-semibold tracking-[0.2em] uppercase px-4 py-1.5 rounded-full mb-5">
                Coming Soon
              </span>
              <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-white mb-3 leading-tight">
                A Story of Survival, Faith &amp; Divine Intervention
              </h2>
              <p className="font-sans text-base text-white/60 leading-relaxed mb-5">
                A gripping true story of faith under fire, fierce resilience, and the kind of divine protection that leaves no doubt God was present in every chapter. A deeply personal testimony written to move, strengthen, and inspire.
              </p>

              {/* WHY THIS BOOK FIRST — foundation callout */}
              <div
                className="border-l-[3px] border-[#C59B76] pl-5 py-1 mb-5"
                data-testid="bp-why-first-callout"
              >
                <p className="font-sans text-[10px] tracking-[0.28em] uppercase text-[#C59B76] font-semibold mb-3">
                  Before the Storm &middot; Why This Book First
                </p>
                <p className="font-serif text-base italic text-white/75 leading-relaxed">
                  Long before a son fought for his life, God was preparing a mother to fight for him. This book is not a detour on the way to Jarvis&rsquo;s story &mdash; it is the ground his story grows from. Through abuse, betrayal, divorce, and survival, faith was being built in the dark. Read this first, and you will understand why nothing about what comes next was ever by accident.
                </p>
              </div>

              <p className="font-serif text-base italic text-[#C59B76]/70">
                A testimony forged in survival.
              </p>
            </motion.div>
            <motion.div {...fadeUp} className="flex justify-center order-1 md:order-2">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-br from-[#C59B76]/15 to-transparent rounded-3xl" />
                <img
                  src={BOOK_COVER}
                  alt="A Story of Survival, Faith and Divine Intervention"
                  data-testid="bp-book-one-cover"
                  className="relative rounded-2xl shadow-2xl w-full max-w-[500px]"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ===== SECTION 4: COMING SOON — JARVIS'S STORY ===== */}
      <section className="py-16 md:py-24 bg-[#F5F2EB]" data-testid="bp-jarvis">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-center">
            <motion.div {...fadeUp} className="flex justify-center md:justify-end">
              <div className="w-full max-w-[320px] aspect-[3/4] rounded-2xl border-2 border-dashed border-[#A67A5B]/25 bg-white/70 flex flex-col items-center justify-center p-10 text-center shadow-lg">
                <div className="w-16 h-16 rounded-full bg-[#A67A5B]/10 flex items-center justify-center mb-6">
                  <Star className="text-[#A67A5B]" size={28} />
                </div>
                <p className="font-serif text-xl font-semibold text-[#2A2421] mb-1">
                  Jarvis's Journey
                </p>
                <p className="font-serif text-base text-[#A67A5B] italic">
                  Miracle So Great &mdash; Coming Soon
                </p>
              </div>
            </motion.div>
            <motion.div {...fadeUp}>
              <span className="inline-block border border-[#A67A5B]/40 text-[#A67A5B] text-xs font-semibold tracking-[0.2em] uppercase px-4 py-1.5 rounded-full mb-5">
                Coming Soon
              </span>
              <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-[#2A2421] mb-3 leading-tight">
                Jarvis's Journey
              </h2>
              <p className="font-serif text-lg text-[#A67A5B] italic mb-5">
                Miracle So Great &mdash; Coming Soon
              </p>
              <p className="font-sans text-base text-[#7A736E] leading-relaxed mb-5">
                A future release shaped by faith, perseverance, and purpose. Jarvis's journey will reflect the strength to endure, the grace to keep going, and the kind of testimony that reminds people hope is never wasted.
              </p>
              <p className="font-serif text-base italic text-[#A67A5B]/70">
                A testimony still unfolding.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ===== SECTION 5: STAY CONNECTED ===== */}
      <section className="py-16 md:py-24 bg-[#2A2421]" data-testid="bp-signup">
        <div className="max-w-2xl mx-auto px-6 md:px-12 text-center">
          <motion.div {...fadeUp}>
            <h2 className="font-serif text-2xl sm:text-3xl font-semibold text-white mb-4">
              Stay Connected
            </h2>
            <p className="font-sans text-sm text-white/50 mb-2 leading-relaxed max-w-lg mx-auto">
              Be the first to know when new books, journals, and purpose-driven projects are released.
            </p>
            <p className="font-sans text-xs text-white/30 mb-8">
              Faith-filled releases. New projects. Meaningful updates.
            </p>
            <SignupForm />
          </motion.div>
        </div>
      </section>
    </div>
  );
}

function SignupForm() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/newsletter`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, source: 'gratitude_journal' }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setStatus({ type: 'success', message: data.message });
        setEmail('');
      } else {
        setStatus({ type: 'error', message: 'Something went wrong. Please try again.' });
      }
    } catch {
      setStatus({ type: 'error', message: 'Something went wrong. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto" data-testid="bp-signup-form">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          placeholder="Your email address"
          data-testid="bp-signup-email"
          className="flex-1 bg-white/10 border border-white/20 focus:border-[#C59B76] text-white px-5 py-3 rounded-full outline-none transition-colors duration-300 placeholder:text-white/30 font-sans text-sm"
        />
        <button
          type="submit"
          disabled={loading}
          data-testid="bp-signup-submit"
          className="bg-[#C59B76] text-white px-6 py-3 rounded-full font-medium text-sm tracking-wide hover:bg-[#A67A5B] transition-all duration-300 disabled:opacity-50 whitespace-nowrap"
        >
          {loading ? 'Joining...' : 'Get Release Updates'}
        </button>
      </form>
      {status && (
        <motion.p
          initial={{ opacity: 0, y: 5 }}
          animate={{ opacity: 1, y: 0 }}
          data-testid={`bp-signup-${status.type}`}
          className={`text-xs mt-4 ${status.type === 'success' ? 'text-[#C59B76]' : 'text-red-400'}`}
        >
          {status.message}
        </motion.p>
      )}
    </div>
  );
}
